import { setEventTypesQuery, useEventTypesQuery } from '../../../lib/Queries';
import Loading from '../../../components/Loading';
import useBusinessNavigate, {
    getBusinessUrl,
} from '../../../lib/useBusinessNavigate.js';
import { MINUTES_IN_A_DAY, minutesToDuration } from '../../../lib/DateTime.js';
import ColumnLayout from '../../../components/ColumnLayout.jsx';
import DotDotDotMenu from '../../../components/DotDotDotMenu';
import PencilSquareIcon from '../../../components/icons/PencilSquareIcon';
import TrashIcon from '../../../components/icons/TrashIcon.jsx';
import useDelete from '../../../lib/useDelete.jsx';
import { formatCurrency } from '../../../lib/CurrencyHelpers.js';
import ErrorMessage from '../../../components/ErrorMessage';
import CopyIcon from '../../../components/icons/CopyIcon.jsx';
import {
    useDoDeleteEventType,
    useDoDuplicateEventType,
} from '../../../lib/useEventTypeActions.js';
import DropdownButton from '../../../components/DropdownButton.jsx';
import * as Toast from '../../../lib/Toast.js';
import GroupIcon from '../../../components/icons/GroupIcon.jsx';
import MeetingRoomIcon from '../../../components/icons/MeetingRoomIcon.jsx';
import GraduationCapIcon from '../../../components/icons/GraduationCapIcon.jsx';
import PresentationIcon from '../../../components/icons/PresentationIcon.jsx';
import EditableQuestion from '../../../components/forms/EditableQuestion.jsx';
import Section from '../../../components/forms/Section.jsx';
import SortableContainers from '../../../components/SortableContainers.jsx';
import React, { Children, useState, useEffect } from 'react';
import useBusinessPostApi from '../../../lib/useBusinessPostApi.js';
import {
    ArrowDownIcon,
    ArrowUpIcon,
} from '@heroicons/react/24/outline/index.js';
import UpdateEventTypeCategoryModal from '../../../components/scheduling/UpdateEventTypeCategoryModal.jsx';
import UppercaseIcon from '../../../components/icons/UppercaseIcon.jsx';
import IconSort from '../../../components/icons/IconSort.jsx';
import FeatureIntro from '~/components/FeatureIntro.jsx';
import PageHeading from '~/components/PageHeading';
import InfoMessageBlock from '../../../components/InfoMessageBlock.jsx';
import AssistantBanner from '~/components/assistant/AssistantBanner.jsx';
import { Link } from 'react-router-dom';

const eventTypeIcons = {
    event: <MeetingRoomIcon />,
    class: <GroupIcon />,
    series: <GraduationCapIcon />,
};

const eventTypeTypeNames = {
    event: 'Appointment',
    class: 'Class',
    series: 'Series',
};

const EventTypeRow = ({ type }) => {
    const navigate = useBusinessNavigate();

    const { doDelete, isDeleting } = useDoDeleteEventType(type.id);
    const { doDuplicate, isDuplicating } = useDoDuplicateEventType(type.id);

    const canBeBooked = type.is_class
        ? type.has_upcoming_classes
        : type.bookable_resources?.length > 0;

    return (
        <div className="flex items-center justify-between group pl-8 pr-6 py-3">
            <div
                className="flex-1 cursor-pointer hover:text-primary-700"
                onClick={() => navigate(`/scheduling/event-types/${type.id}`)}
            >
                <IconSort className="cursor-grab text-gray-500 inline-block -ml-8 opacity-0 group-hover:opacity-100" />
                <span className="">{type.name}</span>
                <div className="text-gray-500 text-sm -ml-2">
                    <span>{eventTypeTypeNames[type.type]} &middot; </span>
                    <span>
                        {type.is_variable_multiday
                            ? `variable, ${parseInt(type.duration / MINUTES_IN_A_DAY)}-${type.settings.max_days} days`
                            : minutesToDuration(type.duration)}
                    </span>{' '}
                    {type.padding_start > 0 && (
                        <span className="">
                            &middot; {minutesToDuration(type.padding_start)}{' '}
                            padding before{' '}
                        </span>
                    )}
                    {type.padding_end > 0 && (
                        <span className="">
                            &middot; {minutesToDuration(type.padding_end)}{' '}
                            padding after{' '}
                        </span>
                    )}
                    {type.price > 0 && (
                        <span className="">
                            &middot; {formatCurrency(type.price)}{' '}
                        </span>
                    )}
                    {!canBeBooked && (
                        <ErrorMessage>This cannot be booked</ErrorMessage>
                    )}
                    {type.is_class && !type.has_upcoming_classes && (
                        <ErrorMessage>
                            There are no upcoming times this is offered at
                        </ErrorMessage>
                    )}
                </div>
            </div>
            <DotDotDotMenu
                options={[
                    {
                        name: 'Edit',
                        icon: <PencilSquareIcon />,
                        onClick: () =>
                            navigate(`/scheduling/event-types/${type.id}`),
                    },
                    {
                        name: 'Duplicate',
                        icon: <CopyIcon />,
                        onClick: () => doDuplicate(),
                    },
                    {
                        name: isDeleting ? 'Deleting...' : 'Delete',
                        isDelete: true,
                        onClick: doDelete,
                    },
                ]}
            />
        </div>
    );
};

const EventTypeCategory = ({
    category,
    children,
    onRename,
    onMoveUp,
    onMoveDown,
}) => {
    const { saveDelete, isDeleting } = useDelete(
        `/event-types/category/${category?.id}/delete`,
        {
            prompt: 'Are you sure you want to delete this category?\nAny types within it will not be deleted and be moved to be uncategorized.',
        },
    );

    function doDelete() {
        saveDelete().then(result => {
            setEventTypesQuery(result, true);
        });
    }

    const menuOptions = [];
    if (onMoveUp) {
        menuOptions.push({
            name: 'Move Up',
            icon: <ArrowUpIcon />,
            onClick: onMoveUp,
        });
    }
    if (onMoveDown) {
        menuOptions.push({
            name: 'Move Down',
            icon: <ArrowDownIcon />,
            onClick: onMoveDown,
        });
    }

    return (
        <div className="mb-6">
            {!!category && (
                <div className="flex px-6 max-w-2xl items-center justify-between">
                    <h3 className="mb-2 text-xl">{category?.name || ''}</h3>
                    <DotDotDotMenu
                        options={[
                            ...menuOptions,
                            {
                                name: 'Rename',
                                icon: <UppercaseIcon />,
                                onClick: onRename,
                            },
                            {
                                name: 'Delete',
                                isDelete: true,
                                onClick: doDelete,
                            },
                        ]}
                    />
                </div>
            )}

            <div className="panel w-full divide-y">
                {children}
                {Children.count(children) === 0 && (
                    <div className="py-3 px-6 text-gray-500">
                        There are no types in this category
                    </div>
                )}
            </div>
        </div>
    );
};

const EventTypesPage = ({}) => {
    const { data, isLoading } = useEventTypesQuery(true);
    const [showNewCategory, setShowNewCategory] = useState(false);
    const [editCategory, setEditCategory] = useState(null);
    const navigate = useBusinessNavigate();
    const { save: saveReorder, isSaving: isReordering } =
        useBusinessPostApi(`/event-types/reorder`);
    const types = data?.types || [];
    const categories = data?.categories || [];

    // Check if we should show the "first event type" congratulations message
    const [showAvailabilityNudge, setShowAvailabilityNudge] = useState(false);

    useEffect(() => {
        const url = new URL(document.location.href);
        const firstEventType = url.searchParams.get('showAvailabilityNudge');
        if (firstEventType === 'true') {
            setShowAvailabilityNudge(true);
            // Clean up the URL parameter without reloading
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('showAvailabilityNudge');
            window.history.replaceState({}, '', newUrl.toString());
        }
    }, []);

    // Only include the default category as empty if we don't have any other categories
    const typesByCategory = { ' 0': [] };
    for (const category of categories) {
        // Ensure we represent empty categories, and preferably keep category order
        typesByCategory[' ' + category.id] = [];
    }

    for (const t of types) {
        typesByCategory[' ' + (t.event_type_category_id || '0')] = [
            ...(typesByCategory[' ' + (t.event_type_category_id || '0')] || []),
            t,
        ];
    }

    if (typesByCategory[' 0'].length === 0 && categories.length > 0) {
        // Remove default if it's empty and there are other categories
        delete typesByCategory[' 0'];
    }

    function onReorder(newItems) {
        let newTypes = [];
        const typeOrder = {};
        // Update both the local state for the category on each item
        //  and also format the data to send to the server
        for (const categoryId of Object.keys(newItems)) {
            typeOrder[parseInt(categoryId)] = newItems[categoryId].map(
                (type, idx) => ({ type: type.id, order: idx }),
            );
            newTypes = [
                ...newTypes,
                ...newItems[categoryId].map(i => ({
                    ...i,
                    event_type_category_id: parseInt(categoryId),
                })),
            ];
        }
        // Send update to the server
        saveReorder({
            order: typeOrder,
        });

        // Update local cache
        setEventTypesQuery({ ...data, types: newTypes }, true);
    }

    function moveCategory(categoryId, offset) {
        const newCategories = [...categories];
        categoryId = parseInt(categoryId);

        const idx = newCategories.findIndex(c => parseInt(c.id) === categoryId);
        const newIdx = idx + offset;
        newCategories.splice(newIdx, 0, newCategories.splice(idx, 1)[0]);

        // Update locally
        setEventTypesQuery({ ...data, categories: newCategories }, true);

        // Send to the server
        saveReorder({
            categoryOrder: newCategories.map((c, idx) => ({
                category: c.id,
                order: idx,
            })),
        });
    }

    return (
        <div className="pb-8">
            <PageHeading
                heading="Event Types"
                rightContent={
                    <div className="flex items-center gap-x-4">
                        <DropdownButton
                            btnContent={
                                <span>
                                    Add
                                    <span className="hidden sm:inline">
                                        {' '}
                                        Event Type
                                    </span>
                                </span>
                            }
                            btnClassName="btn-pill"
                            menuItemsClassName="w-72"
                            iconsFirst={true}
                            options={[
                                {
                                    name: 'Appointment',
                                    description:
                                        'Individual booking that follows Availability',
                                    icon: <MeetingRoomIcon />,
                                    onClick: () =>
                                        navigate(`/scheduling/event-types/0`),
                                },
                                {
                                    name: 'Class or Workshop',
                                    description:
                                        'A group class. Clients book any session.',
                                    icon: <GroupIcon />,
                                    onClick: () =>
                                        navigate(
                                            '/scheduling/event-types/0?type=class',
                                        ),
                                },
                                {
                                    name: 'Course or Series of Classes',
                                    description:
                                        'A set of group classes where clients must attend all sessions.',
                                    icon: <GraduationCapIcon />,
                                    onClick: () =>
                                        navigate(
                                            '/scheduling/event-types/0?type=series',
                                        ),
                                },
                                // {
                                //     name: 'Workshop',
                                //     description: 'A one-time group session, typically a special event.',
                                //     icon: <PresentationIcon />,
                                //     onClick: () => navigate('/scheduling/event-types/0?type=class')
                                // },
                            ]}
                        />

                        <button
                            type="button"
                            className="hidden sm:block btn-light-pill"
                            onClick={() => {
                                setShowNewCategory(true);
                                setEditCategory(null);
                            }}
                        >
                            Add Category
                        </button>
                    </div>
                }
            />

            <FeatureIntro>
                Event types are the services or classes you offer. Clients
                usually begin the scheduling process by choosing which event
                type they want to book.
            </FeatureIntro>

            {showAvailabilityNudge && (
                <InfoMessageBlock showIcon={true} className="mb-4 max-w-2xl">
                    Congratulations on setting up your first event type!
                    <br />
                    <br />
                    Next step: set your availability so clients can book
                    appointments with you.{' '}
                    <Link
                        to={getBusinessUrl('/scheduling/availability')}
                        className="underline"
                    >
                        Go to Availability →
                    </Link>
                </InfoMessageBlock>
            )}

            <AssistantBanner
                className="mb-6"
                description="Tell Breely's Assistant what you want to offer, how it should be booked, who or what can be booked, when it's available, and it'll set up the event for you."
                id="event-types"
                prompt="Help me set up a new event type."
            />

            {isLoading && <Loading />}
            {!isLoading && types?.length === 0 && (
                <div className="panel p-4 text-gray-500">
                    <ErrorMessage>
                        There are no event types, please add one to allow
                        clients to book.
                    </ErrorMessage>
                </div>
            )}
            {!isLoading && types?.length > 0 && (
                <SortableContainers
                    items={typesByCategory}
                    setItems={onReorder}
                    renderItem={({ item, isActive }) => (
                        <EventTypeRow type={item} />
                    )}
                    Container={({ listId, idx, children }) => {
                        const category = categories.find(
                            c => parseInt(c.id) === parseInt(listId),
                        );

                        return (
                            <EventTypeCategory
                                category={category}
                                children={children}
                                onRename={() => {
                                    setEditCategory(category);
                                    setShowNewCategory(true);
                                }}
                                onMoveUp={
                                    idx > 1
                                        ? () => moveCategory(listId, -1)
                                        : null
                                }
                                onMoveDown={
                                    idx <
                                    Object.keys(typesByCategory).length - 1
                                        ? () => moveCategory(listId, 1)
                                        : null
                                }
                            />
                        );
                    }}
                />
            )}

            <UpdateEventTypeCategoryModal
                isOpen={showNewCategory}
                onClose={() => setShowNewCategory(false)}
                category={editCategory}
            />
        </div>
    );
};

export default EventTypesPage;
